import { FC, useCallback, useState } from 'react';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftOrderForAdmin } from '@shared/api/giftOrder/giftOrder.interface';
import { api_ } from '@shared/plugIn/axios';

import { getOrderType } from '../utils/giftOrder.constants';

export const CreateCouponTestModal: FC<{
  orders: GiftOrderForAdmin[];
  onClose: () => void;
}> = ({ orders, onClose }) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});

  const couponOrders = orders.filter((order) => {
    const type = getOrderType(order);
    return type === 'coupon' || type === 'mixed';
  });

  const handleCreate = useCallback(async (orderId: string) => {
    setLoading(orderId);
    try {
      const res = await api_.post(`/admin/gift-order/create-coupons/${orderId}`);
      setResults((prev) => ({
        ...prev,
        [orderId]: `성공 (${JSON.stringify(res.data?.count ?? res.data)})`,
      }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '실패';
      setResults((prev) => ({ ...prev, [orderId]: `오류: ${msg}` }));
    } finally {
      setLoading(null);
    }
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-bold text-gray-900">[TEST] 쿠폰형 주문 Coupon 생성</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>
        <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(80vh - 56px)' }}>
          <p className="mb-3 text-xs text-red-500">
            * 테스트 전용 - POST /admin/gift-order/create-coupons/:id 호출
          </p>
          {couponOrders.length === 0 ? (
            <p className="py-8 text-center text-gray-500">쿠폰형 주문이 없습니다.</p>
          ) : (
            <div className="space-y-2">
              {couponOrders.map((order) => {
                const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
                const totalQty = order.orderDetails.reduce((s, d) => s + d.quantity, 0);
                const couponCount =
                  (order.coupons as GiftCouponSummary[] | undefined)?.length ?? 0;
                const result = results[order._id];
                const isLoading = loading === order._id;

                return (
                  <div
                    key={order._id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {firstItemName}
                      </p>
                      <p className="text-xs text-gray-500">
                        수량: {totalQty}개 | 기존 coupon: {couponCount}개
                      </p>
                      <p className="truncate text-[10px] text-gray-400">
                        ID: {order._id}
                      </p>
                      {result && (
                        <p
                          className={`mt-1 text-xs font-medium ${
                            result.startsWith('성공') ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {result}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleCreate(order._id)}
                      disabled={isLoading}
                      className={`ml-3 shrink-0 rounded-md px-3 py-1.5 text-xs font-medium text-white ${
                        isLoading
                          ? 'cursor-not-allowed bg-gray-400'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {isLoading ? '생성중...' : 'Coupon 생성'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
